import {
  type HasImageUrl,
  type HasMockup,
  type HasPalette,
  type HasProduct,
  type HasThingType,
  type Mockup,
  type Palette,
  type Image,
  type PortfolioProjectImage,
  type PortfolioProjectMockup,
  type PortfolioProjectPalette,
  type PortfolioProjectProduct,
  type PortfolioProjectThing,
  type Product,
  type ProjectImage,
  type ProjectMockup,
  type ProjectPalette,
  type ProjectProduct,
  type ProjectThing,
  type Thing,
  type ThingType,
  type ProjectThingType,
  type ProjectGenericThing,
  type HasProjectThingType,
  type HasThing,
  type MockupType,
} from './types'
import {
  type ShortDesignerInfo,
  type ShortProjectInfo,
} from '/@/data/designer/types'
import type { StellaIconId } from '/@/stella/icons/types'
import { toTitleCase } from '/@/utils/misc'

export function hasThing(t: unknown): t is HasThing {
  return Boolean((t as HasThing).thing)
}

export function hasProduct(t: unknown): t is HasProduct {
  return Boolean((t as HasProduct).product)
}

export function hasMockup(t: unknown): t is HasMockup {
  return Boolean((t as HasMockup).mockup)
}

export function hasPalette(t: unknown): t is HasPalette {
  return Boolean((t as HasPalette).palette)
}

export function hasImageUrl(t: unknown): t is HasImageUrl {
  return Boolean((t as HasImageUrl).imageUrl)
}

export function isProduct(
  thing: ProjectThing | PortfolioProjectThing,
): thing is ProjectProduct | PortfolioProjectProduct
export function isProduct(thing: ProjectThing): thing is ProjectProduct
export function isProduct(
  thing: PortfolioProjectThing,
): thing is PortfolioProjectProduct
export function isProduct(thing: Thing): thing is Product
export function isProduct(thing: Thing | ProjectThing): thing is Product
export function isProduct(thing: HasThingType | HasProjectThingType) {
  return thing.thingType === 'PRODUCT'
}

export function isPalette(
  thing: ProjectThing | PortfolioProjectThing,
): thing is ProjectPalette | PortfolioProjectPalette
export function isPalette(thing: ProjectThing): thing is ProjectPalette
export function isPalette(
  thing: PortfolioProjectThing,
): thing is PortfolioProjectPalette
export function isPalette(thing: Thing): thing is Palette
export function isPalette(thing: HasThingType | HasProjectThingType) {
  return thing.thingType === 'PALETTE'
}

export function isMockup(
  thing: ProjectThing | PortfolioProjectThing,
): thing is ProjectMockup | PortfolioProjectMockup
export function isMockup(thing: Thing | ProjectThing): thing is Mockup
export function isMockup(thing: ProjectThing): thing is ProjectMockup
export function isMockup(
  thing: PortfolioProjectThing,
): thing is PortfolioProjectMockup
export function isMockup(thing: Thing): thing is Mockup
export function isMockup(thing: HasThingType | HasProjectThingType) {
  return thing.thingType === 'MOCKUP'
}

export function isImage(
  thing: ProjectThing | PortfolioProjectThing,
): thing is ProjectImage | PortfolioProjectImage
export function isImage(thing: Thing | ProjectThing): thing is Image
export function isImage(thing: ProjectThing): thing is ProjectImage
export function isImage(
  thing: PortfolioProjectThing,
): thing is PortfolioProjectImage
export function isImage(thing: Thing): thing is Image
export function isImage(thing: HasThingType | HasProjectThingType) {
  return thing.thingType === 'IMAGE'
}

export function isProjectGenericThing(
  thing: HasProjectThingType,
): thing is ProjectGenericThing {
  return thing.thingType === 'GENERIC'
}

export function isThingType(type?: string | null): type is ThingType {
  if (!type) return false
  return ['PRODUCT', 'IMAGE', 'PALETTE', 'MOCKUP'].includes(type)
}

function thingName(thing: Thing) {
  return thing.name
}

function projectThingName(thing: ProjectThing) {
  return isPalette(thing)
    ? thingName(thing.thing)
    : (thing.name ??
        (isProjectGenericThing(thing) ? '' : thingName(thing.thing)))
}

export function getThingName(thing: Thing | ProjectThing): string {
  if (isProjectThing(thing)) return projectThingName(thing)
  else return thingName(thing)
}

export function isProjectThingType(
  type?: string | null,
): type is ProjectThingType {
  if (!type) return false
  return ['PRODUCT', 'IMAGE', 'PALETTE', 'MOCKUP', 'GENERIC'].includes(type)
}

export function prettyThingType(
  type: ThingType | ProjectThingType | 'PROJECT',
  plural: boolean = false,
): string {
  const end = plural ? 's' : ''
  switch (type) {
    case 'IMAGE':
      return `Image${end}`
    case 'MOCKUP':
      return `Vizi${end} & Plan${end}`
    case 'PALETTE':
      return `Color Pal${end}`
    case 'PRODUCT':
      return `Product${end}`
    case 'GENERIC':
      return `Line Item${end}`
    case 'PROJECT':
      return `Project${end}`
    default:
      return `${toTitleCase(type)}${end}`
  }
}

export function prettyViziType(
  type?: MockupType,
  plural: boolean = false,
): string {
  const end = plural ? 's' : ''
  switch (type) {
    case 'FLOOR_PLAN':
      return `Floor Plan${end}`
    case 'ELEVATION':
      return `Elevation${end}`
    default:
      return `Vizi${end}`
  }
}

export function isProjectThing(
  thing: Thing | ProjectThing,
): thing is ProjectThing {
  return (thing as ProjectThing).layoutFormat !== undefined
}

export function projectGenericThingStyle(thing: ProjectGenericThing): {
  bgClass: string
  iconClass: string
  icon: StellaIconId
} {
  if (thing.projectGenericThingSource === 'DEFAULT_SHOP_LIST') {
    return {
      bgClass: 'bg-cream-30',
      iconClass: 'text-cream-90',
      icon: 'clipboard',
    }
  } else {
    return {
      bgClass: 'bg-green-10',
      iconClass: 'text-green-50',
      icon: 'cart',
    }
  }
}

interface SearchableThing {
  id: string
  name: string
  permissions?: {
    designer: ShortDesignerInfo
  }[]
  project?: ShortProjectInfo
  designer?: ShortDesignerInfo
}

export function isMatch(thing: SearchableThing, searchRegex: RegExp) {
  if (!searchRegex.source) return true

  const matchesThingName = searchRegex.test(thing.name)

  const matchesThingProjectName =
    thing.project?.name && searchRegex.test(thing.project?.name)

  const matchesThingDesignerNames =
    thing.designer &&
    (searchRegex.test(thing.designer.firstName) ||
      searchRegex.test(thing.designer.lastName))

  const matchesThingPermissionsNames =
    thing.permissions?.length &&
    thing.permissions.some(
      permission =>
        searchRegex.test(permission.designer.firstName) ||
        searchRegex.test(permission.designer.lastName),
    )

  const matchesProjectPermissionsNames =
    thing.project?.permissions?.length &&
    thing.project?.permissions.some(
      permission =>
        searchRegex.test(permission.designer.firstName) ||
        searchRegex.test(permission.designer.lastName),
    )

  return (
    matchesThingName ||
    matchesThingProjectName ||
    matchesThingDesignerNames ||
    matchesThingPermissionsNames ||
    matchesProjectPermissionsNames
  )
}
